// Helpers for leader and wonder effects. An effect adds contributions to a player's numbers, each tagged with the
// card it comes from, so totals can always be broken down by source (the status box tooltips).
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { AgeOrder } = require('../Constants/Constants');

// Which list of contributions each stat is computed from
const StatDelegates = {
    culture: 'cultureProductionCalculationDelegate',
    science: 'scienceProductionCalculationDelegate',
    food: 'foodProductionCalculationDelegate',
    resources: 'resourceProductionCalculationDelegate',
    strength: 'militaryCalculationDelegate',
    happyFaces: 'happyFaceCalculationDelegate',
    colonization: 'colonizationBonusCalculationDelegate',
    civilActions: 'civilActionCalculationDelegate',
    militaryActions: 'militaryActionCalculationDelegate',
    civilHandLimit: 'civilHandLimitCalculationDelegate',
    militaryHandLimit: 'militaryHandLimitCalculationDelegate',
    urbanBuildingLimit: 'urbanBuildingLimitCalculationDelegate',
    // Discontent workers not counted (Forbidden City)
    ignoredDiscontent: 'ignoredDiscontentCalculationDelegate',
    blueTokens: 'blueTokenCalculationDelegate',
    yellowTokens: 'yellowTokenCalculationDelegate',
};

// Adds a contribution from 'source' to 'stat': a fixed number, or a function of the player worked out whenever the
// stat is (so it follows the board as it changes)
const Add = (player, source, stat, amount) => {
    player[StatDelegates[stat]].push({
        Source: source,
        Action: typeof amount === 'function' ? () => amount(player) : () => amount,
    });
};

// For a card that makes other cards produce more (e.g. Chaplin: "your best theater produces twice as much"): adds
// what each of 'cards' produces extra, perCard(player, card). The producing card is credited with it, and the source
// is noted as where the extra comes from, e.g. "Movies: 8 (4 from Charles Chaplin)"; it also counts as what that card
// produces (Hollywood, Internet).
// perWorker: for an amount that is so much for each worker on the card, that amount (see AddPerWorker)
const AddThrough = (player, source, stat, cards, perCard, perWorker) => cards.forEach(card => {
    player[StatDelegates[stat]].push({ Source: source, Through: card, Action: () => perCard(player, card),
        ...(perWorker ? { PerWorker: () => perWorker(player, card) } : {}) });
});

// For a card that gives every worker on other cards something (e.g. Darwin: "each of your labs and libraries produces
// 1 extra culture"): perWorker(player, card) for each worker on each of 'cards', credited like AddThrough. Remembered
// per worker, so a card on the board can say what each worker on it gets, built on or not.
const AddPerWorker = (player, source, stat, cards, perWorker) =>
    AddThrough(player, source, stat, cards, (p, card) => perWorker(p, card) * card.GetWorkerCount(), perWorker);

// What other cards give each worker on this card: [{ source, stat, value }] (see AddPerWorker)
const PerWorkerEffects = (player, card) => Object.entries(StatDelegates).flatMap(([stat, delegates]) =>
    (player[delegates] ?? []).filter(d => d.Through === card && d.PerWorker)
        .map(d => ({ source: d.Source?.name ?? "Unknown", stat, value: d.PerWorker() })))
    .filter(effect => effect.value !== 0);

// The player's technology cards of these types (built on or not)
const CardsOf = (player, types) => player.technologies.filter(tech => types.includes(tech.type));

// What the player's cards of these types produce for a stat, including what other cards add through them
// (e.g. culture from theaters, with Chaplin's doubling of the best one)
const ProducedBy = (delegates, types) => delegates
    .filter(d => types.includes((d.Through ?? d.Source)?.type))
    .reduce((sum, d) => sum + d.Action(), 0);

// Extra resources actually produced at the end of the turn by some of the player's cards because of another card
// (e.g. Bill Gates's labs), shown and credited like AddThrough. Runs after the cards' own production (lower Priority).
const AddResourceProductionThrough = (player, source, cards, perCard) => {
    AddThrough(player, source, 'resources', cards, perCard);
    player.resourceProductionDelegate.push({
        Source: source,
        Priority: -1,
        Action: () => player.GainResources(cards.reduce((sum, card) => sum + perCard(player, card), 0)),
    });
};

// Kinds of cost a card can change (see Moves/Costs.js)
const CostKinds = {
    TakeCard: 'takeCard',               // civil actions to take a card from the card row; context { card, base }
    PopulationFood: 'populationFood',   // food to increase population
    BuildCost: 'buildCost',             // a card's resource cost per building or unit, also used for upgrades; context { tech }
    BuildPayment: 'buildPayment',       // resources paid to build or upgrade anything; context { tech }
    WonderPayment: 'wonderPayment',     // resources paid for wonder stages
    DevelopScience: 'developScience',   // science to develop a technology; context { tech }
};

// Changes a kind of cost by 'amount' (negative for a discount): a number, or a function of the context.
// once: a name for a change usable once per turn (e.g. Homer's extra resource).
const AddCostModifier = (player, source, kind, amount, once) => {
    player.costModifierDelegate.push({
        Source: source,
        kind,
        Action: typeof amount === 'function' ? (context) => amount(context, player) : () => amount,
        once,
    });
};

// Rules a card in play can lift for its owner (see AddRuleException), e.g. an expansion wonder letting action cards be
// played the turn they are taken
const Rules = {
    PlayActionCardsTakenThisTurn: 'playActionCardsTakenThisTurn',
    // A revolution costs all military actions instead of all civil actions (Robespierre)
    RevolutionWithMilitaryActions: 'revolutionWithMilitaryActions',
    // Infantry can count as cavalry when forming armies for tactics (Genghis Khan)
    InfantryAsCavalry: 'infantryAsCavalry',
    // The player may not play aggressions or wars (Gandhi)
    NoAggression: 'noAggression',
    // Rivals pay twice as many military actions to play an aggression or war against the player (Gandhi)
    DoubleAggressionCost: 'doubleAggressionCost',
    // Rivals pay 3 more military actions to play an aggression or war against the player (Gandhi, the expansion's)
    ExtraAggressionCost: 'extraAggressionCost',
    // The tactic played stays the player's alone: the step of making it available to everyone at the start of the turn is
    // skipped (Sun Tzu)
    KeepTacticPrivate: 'keepTacticPrivate',
    // Any military card can be prepared as an event (Confucius); revealed, one that is not an event gives everyone
    // 1 science
    PrepareAnyMilitaryCard: 'prepareAnyMilitaryCard',
    // Each farm (a worker on a farm) can count as an Age A infantry or artillery unit when forming armies for tactics
    // (Jan Žižka)
    FarmsAsUnits: 'farmsAsUnits',
    // One infantry, cavalry or artillery unit counts twice when forming armies for tactics (Marlene Dietrich)
    DoubleOneUnit: 'doubleOneUnit',
    // Labs of Age I or higher are used as mines: they produce, store and make change with blue tokens worth their level
    // (Bill Gates; tokens already on them stay usable after he leaves play)
    LabsAsMines: 'labsAsMines',
};

// While the source is in play, its owner is exempt from the rule
const AddRuleException = (player, source, rule) => player.ruleExceptionDelegate.push({ Source: source, rule });

// Whether a card in play exempts the player from the rule
const HasRuleException = (player, rule) => player.ruleExceptionDelegate.some(exception => exception.rule === rule);

// Extra resources a player has for some kinds of spending only (not a discount: the cost stays the same, and the pool
// pays for part of it). Shown beside the resource counter.
const PoolKinds = {
    MilitaryResources: 'militaryResources', // building and upgrading military units
    MilitaryScience: 'militaryScience',     // developing military unit technologies
    WonderUrbanResources: 'wonderUrbanResources', // building wonders, and building and upgrading urban buildings (Cleopatra)
    // Any payment in this currency, paid instead with another (payWith; Trade Routes Agreement). Only used when the
    // player chooses to (the move's tradeRoute).
    Resources: 'resources',
    Food: 'food',
};

// While the source is in play, its owner has 'amount' extra each turn for that kind of spending (e.g. Homer)
// payWith: what the pool is paid with instead ('food' / 'resources'), for pools that swap one currency for another
const AddResourcePool = (player, source, kind, amount, payWith) =>
    player.resourcePoolDelegate.push({ Source: source, kind, Action: () => amount, payWith });

// Things done this turn that cards care about (see PlayerState.turnFlags), besides once-per-turn discounts used
const TurnFlags = {
    ReplacedLeader: 'replacedLeader',
    MilitaryAsCivil: 'militaryAsCivil', // Hammurabi's military action used as a civil action
    OceanLiner: 'oceanLiner',           // Ocean Liner's free population
    BachUpgrade: 'bachUpgrade',         // Bach's upgrade to a theater
    Churchill: 'churchill',             // Churchill's science and resources taken (so no culture)
    CaesarSecond: 'caesarSecond',       // a political action played: Julius Caesar's second one may follow
    Gutenberg: 'gutenberg',             // Gutenberg's civil action (for labs and libraries) used
    Developed: 'developed',             // a technology developed (one each time; the Nobel Prize counts them)
    ActionCardPlayed: 'actionCardPlayed', // an action card played this turn (Silk Road: only the first gives more)
    SilkRoadStarted: 'silkRoadStarted', // Silk Road's first stage built this turn: the first action card after it gives more
    Harvard: 'harvard',                 // an action card taken this turn played, once a turn (Harvard College)
    RedCross: 'redCross',               // a stage of another player's International Red Cross built (once a turn)
    PoliticalGivenUp: 'politicalGivenUp', // the political phase was skipped, given up for International Agreement
};

// Things that happen during a turn that cards can react to (see AddTrigger), and the context each passes
const TriggerEvents = {
    TakeCard: 'takeCard',             // a card taken from the card row; { card }
    Develop: 'develop',               // a technology developed; { tech }
    EndOfTurn: 'endOfTurn',           // the player's turn is ending, before production
    LeaderLeaves: 'leaderLeaves',     // the leader in play is being replaced; { leader }
    Revolution: 'revolution',         // the player declared a revolution; { government }
    PopulationIncrease: 'populationIncrease', // the population grew (also for free, e.g. from a colony); { free }
    GameEnd: 'gameEnd',               // final scoring, after the last turn
    TurnStart: 'turnStart',           // the player's turn begins (before the political phase)
    ActionPhaseStart: 'actionPhaseStart', // the player's action phase begins
    PrepareEvent: 'prepareEvent',     // the player prepared an event (or another card, as one); { card }
    DiscardMilitary: 'discardMilitary', // military cards discarded at the end of the turn, over the hand limit; { count }
    AgeStart: 'ageStart',             // a new age has begun (its decks in play); { age } (see GameState StartNextAge)
};

// Reacts to an event while the source is in play: action(player, context) changes the state and returns what it did
// as short text for the move log (e.g. "+1 science"), or nothing when it did nothing, or { note } to explain why it
// did nothing where that is worth knowing beforehand (shown in the end-of-turn preview, not logged).
// preview(player, context): what it would do, as the same short text, without changing anything (shown on the button of
// an action that sets it off, e.g. Increase Population with Maria Theresa), or nothing
const AddTrigger = (player, source, event, action, preview) => {
    player.triggerDelegate.push({ Source: source, event, Action: (context) => action(player, context),
        ...(preview ? { Preview: (context) => preview(player, context) } : {}) });
};

// What the player's cards would do on an event, from their previews: ["Maria Theresa: +1 science, +1 culture"]
const PreviewTriggers = (player, event, context = {}) => player.triggerDelegate
    .filter(trigger => trigger.event === event && trigger.Preview)
    .map(trigger => { const text = trigger.Preview(context); return text ? `${trigger.Source?.name}: ${text}` : null; })
    .filter(text => text);

// Runs the player's reactions to an event, noting each in the game state's trigger log (added to the move's
// description, e.g. "Aristotle: +1 science")
const FireTriggers = (player, event, context = {}) => {
    player.triggerDelegate
        .filter(trigger => trigger.event === event)
        .forEach(trigger => LogTrigger(player, trigger.Source?.name, trigger.Action(context)));
};

// Notes what a card did in the move log, or why it did nothing (see AddTrigger)
const LogTrigger = (player, source, text) => {
    if (!text || !player.gameState)
    {
        return;
    }
    if (text.note)
    {
        player.gameState.triggerNotes.push(`${source}: ${text.note}`);
    } else {
        player.gameState.triggerLog.push(`${source}: ${text}`);
    }
};

// Strength a card adds only in some situations (not to the strength shown on the board), e.g. Nostradamus's +3 when
// events pick the weakest
const Situations = {
    EventWeakest: 'eventWeakest',         // an event working out who is the weakest
    DefendAggression: 'defendAggression', // being the target of an aggression (also whether it can be played at all)
};
const AddSituationalStrength = (player, source, situation, amount) =>
    player.situationalStrengthDelegate.push({ Source: source, situation, Action: () => amount });

// The player's strength in that situation: their strength plus what cards add then
const SituationalStrength = (player, situation) => player.GetMilitaryStrength()
    + player.situationalStrengthDelegate.filter(d => d.situation === situation).reduce((sum, d) => sum + d.Action(), 0);

// An extra civil action each turn for some actions only (Gutenberg: labs and libraries). eligible(context) says whether
// an action may use it ({ card } taking a card, { tech } developing, building or upgrading); it is used first for
// those, and then flag is set for the turn; onUse(player) does what using it gives, returning text for the log.
// It is shown as a token of its own, apart from the usual civil actions (see PlayerState.GetCivilActionsRemaining).
// gain: what using it gives, for the buttons of the actions it would pay for (e.g. "+2 culture")
const AddRestrictedCivilAction = (player, source, { flag, eligible, onUse, purpose, gain }) =>
    player.civilActionCalculationDelegate.push({ Source: source, Restricted: true, flag, eligible, onUse, purpose, gain, Action: () => 1 });

// Several fixed bonuses at once, e.g. Bonuses(player, card, { culture: 1, happyFaces: 2 })
const Bonuses = (player, source, bonuses) =>
    Object.entries(bonuses).forEach(([stat, amount]) => Add(player, source, stat, amount));

const MilitaryTypes = Object.values(TechnologyTypes.Military);
const { Lab, Library, Temple, Theater } = TechnologyTypes.Urban;

// A card's level: Age A 0, Age I 1, Age II 2, Age III 3
const Level = (card) => AgeOrder.indexOf(card.age);

// Workers on the player's cards of these types
const WorkersOn = (player, types) => player.technologies
    .filter(tech => types.includes(tech.type))
    .reduce((sum, tech) => sum + tech.GetWorkerCount(), 0);

// The player's built cards (at least one worker) of these types
const Built = (player, types) => player.technologies.filter(tech => types.includes(tech.type) && tech.GetWorkerCount() > 0);

// The highest-level built card of these types, or undefined
const Best = (player, types) => Built(player, types).sort((a, b) => Level(b) - Level(a))[0];

module.exports = {
    Add,
    AddThrough,
    AddPerWorker,
    PerWorkerEffects,
    CardsOf,
    ProducedBy,
    AddResourceProductionThrough,
    AddCostModifier,
    CostKinds,
    TurnFlags,
    PoolKinds,
    AddResourcePool,
    Rules,
    AddRuleException,
    HasRuleException,
    TriggerEvents,
    AddTrigger,
    FireTriggers,
    PreviewTriggers,
    LogTrigger,
    Bonuses,
    Level,
    WorkersOn,
    Built,
    Best,
    MilitaryTypes,
    Lab,
    Library,
    Temple,
    Theater,
    Situations,
    AddSituationalStrength,
    SituationalStrength,
    AddRestrictedCivilAction,
}